import { useState } from 'react'

import { ШКАЛЫ, ru, scaleDivision, scaleReading } from '../../lib/physics'
import { Choice, Predict } from './common'

/**
 * Цена деления любой шкалы (урок 4): термометр, линейка, мерный цилиндр, шприц. Ученик сам
 * считает цену деления — два ближайших штриха с числами, разность на число делений — и по ней
 * читает показание. Шкала нарочно начинается не всегда с нуля.
 */

const X0 = 24
const ДЛИНА = 312

/** Число из поля ввода: запятая или точка; пусто или мусор — null. */
function число(s: string): number | null {
  const x = Number(s.trim().replace(',', '.'))
  return s.trim() === '' || !Number.isFinite(x) ? null : x
}

/** Случайное показание на шкале — ровно на штрихе, не у самых краёв. */
function новоеЗначение(номер: number): number {
  const ш = ШКАЛЫ[номер]
  const деление = ш.подписи / ш.делений
  const всего = Math.round((ш.до - ш.от) / деление)
  return scaleReading(ш.от + деление * (2 + Math.floor(Math.random() * (всего - 4))), деление)
}

export default function DivisionLab() {
  const [номер, setНомер] = useState(0)
  const [значение, setЗначение] = useState(() => новоеЗначение(0))
  const [цена, setЦена] = useState('')
  const [показ, setПоказ] = useState('')
  const [проверено, setПроверено] = useState(false)

  const ш = ШКАЛЫ[номер]
  const деление = scaleDivision(ш.от + ш.подписи, ш.от, ш.делений)
  const всего = Math.round((ш.до - ш.от) / деление)
  const xЗнач = (v: number) => X0 + ((v - ш.от) / (ш.до - ш.от)) * ДЛИНА
  const знаков = деление < 0.1 + 1e-9 ? 1 : деление < 1 ? 1 : 0

  const сменить = (n: number) => {
    setНомер(n)
    setЗначение(новоеЗначение(n))
    setЦена('')
    setПоказ('')
    setПроверено(false)
  }
  const верноЦена = (() => {
    const x = число(цена)
    return x !== null && Math.abs(x - деление) < 1e-9
  })()
  const верноПоказ = (() => {
    const x = число(показ)
    return x !== null && Math.abs(x - значение) <= деление / 2 + 1e-9
  })()

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 120" role="img" aria-label={`Шкала: ${ш.прибор}`}>
        <rect x={X0 - 12} y={30} width={ДЛИНА + 24} height={60} rx={8} className="fill-cabin ink" strokeWidth={1.5} />
        {/* столбик или указатель до показания */}
        <rect x={X0} y={52} width={xЗнач(значение) - X0} height={8} rx={3} className={ш.единица === '°C' ? 'fill-heavy' : 'fill-earth'} />
        {Array.from({ length: всего + 1 }, (_, i) => {
          const v = ш.от + i * деление
          const подписан = i % ш.делений === 0
          return (
            <g key={i}>
              <line x1={xЗнач(v)} y1={62} x2={xЗнач(v)} y2={подписан ? 78 : 70} className="ink" strokeWidth={подписан ? 1.4 : 0.7} />
              {подписан && <text x={xЗнач(v)} y={88} textAnchor="middle" className="lab-tick">{ru(v, 0)}</text>}
            </g>
          )
        })}
        <text x={X0 + ДЛИНА} y={46} textAnchor="end" className="lab-tick">{ш.единица}</text>
        <text x={X0} y={22} className="lab-label">{ш.прибор}</text>
      </svg>

      <label className="lab-slider">
        <span>Цена деления, {ш.единица}</span>
        <input inputMode="decimal" value={цена} placeholder="разность подписей : число делений" onChange={(e) => { setЦена(e.target.value); setПроверено(false) }} />
      </label>
      <label className="lab-slider">
        <span>Показание, {ш.единица}</span>
        <input inputMode="decimal" value={показ} placeholder="где кончается столбик" onChange={(e) => { setПоказ(e.target.value); setПроверено(false) }} />
      </label>
      <div className="row-wrap" style={{ marginTop: 10 }}>
        <button className="btn btn-primary" onClick={() => setПроверено(true)}>Проверить</button>
        <button className="btn" onClick={() => { setЗначение(новоеЗначение(номер)); setПоказ(''); setПроверено(false) }}>🎲 Другое показание</button>
      </div>
      {проверено && (
        <div className="lab-verdict-list">
          <div className={верноЦена ? 'lab-verdict ok' : 'lab-verdict'}>
            {верноЦена
              ? `✅ Цена деления ${ru(деление, знаков)} ${ш.единица}. Точность отсчёта — половина деления, ${ru(деление / 2, знаков + 1)} ${ш.единица}.`
              : `Возьми соседние подписи, например ${ru(ш.от, 0)} и ${ru(ш.от + ш.подписи, 0)}: разность ${ru(ш.подписи, 0)} ${ш.единица}, между ними ${ш.делений} делений — промежутков, а не штрихов.`}
          </div>
          <div className={верноПоказ ? 'lab-verdict ok' : 'lab-verdict'}>
            {верноПоказ
              ? `✅ Показание ${ru(значение, знаков)} ${ш.единица}.`
              : 'Найди ближайшую подпись левее конца столбика и прибавь число делений до него, умноженное на цену деления.'}
          </div>
        </div>
      )}

      <Predict
        question="На шприце между отметками 4 мл и 5 мл пять делений. Какова цена деления?"
        options={['1 мл', '0,25 мл', '0,2 мл']}
        right={2}
        revealed={проверено && верноЦена && ш.прибор === 'шприц на 5 мл'}
        explain="(5 мл − 4 мл) : 5 = 0,2 мл. Штрихов между отметками четыре, а делений — пять."
      />

      <div className="lab-sub">Прибор</div>
      <Choice options={ШКАЛЫ.map((s, i) => ({ value: i, label: s.прибор }))} value={номер} onChange={сменить} />
      <p className="muted small">
        Деление — промежуток между соседними штрихами. Цена деления — сколько единиц величины
        приходится на одно деление: чем она меньше, тем точнее прибор.
      </p>
    </div>
  )
}
